import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getSlidesTemplatePack } from '../decks/getSlidesTemplatePack';
import { buildEnhancedSlides } from './buildEnhancedSlides';
import { useSwipeNavigation } from '../../hooks/useSwipeNavigation';
import { startDataDashboardEffect } from './effects/dataDashboard';
import { startQuantumEffect } from './effects/quantum';
import { startMatrixRainEffect } from './effects/matrixRain';
import { startCosmicEffect } from './effects/cosmic';
import { startBroadcastInterruptEffect } from './effects/broadcastInterrupt';
import { startWheatPasteEffect } from './effects/wheatPaste';
import { startStencilSprayEffect } from './effects/stencilSpray';
import { startTeletypeAssembleEffect } from './effects/teletypeAssemble';
import { startComparisonSplitEffect } from './effects/comparisonSplit';
import { startPanicSirensEffect } from './effects/panicSirens';
import { startFactoryStampEffect } from './effects/factoryStamp';
import { startParticleSystem } from './effects/particles';
import { startNodeGraphEffect } from './effects/nodeGraph';
import { startCountingEffect } from './effects/countingNumbers';
import { startTerminalTypingEffect } from './effects/terminalTyping';
import { startSystemBootEffect } from './effects/systemBoot';
import { startDangerZoneEffect } from './effects/dangerZone';
import { startModelAnalysisEffect } from './effects/modelAnalysis';
import './SlidesView.css';

function createParagraphs(text) {
  if (!text) return [];
  return String(text)
    .split(/\n\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function createLegacyParagraphs(text, seed) {
  if (!text) return [];
  const t = String(text).trim();
  if (!t) return [];
  const sentences = t.match(/[^.!?]+[.!?]+/g) || [t];

  // Deterministic pseudo-random from seed (so paragraphs stay stable per slide)
  let state = (typeof seed === 'number' ? seed : 0) >>> 0;
  const rand = () => {
    state = (1664525 * state + 1013904223) >>> 0;
    return state / 0xffffffff;
  };

  const paragraphs = [];
  let current = [];
  for (let i = 0; i < sentences.length; i++) {
    current.push(String(sentences[i]).trim());
    if (current.length >= 2 && (i === sentences.length - 1 || rand() > 0.5)) {
      paragraphs.push(current.join(' '));
      current = [];
    }
  }
  if (current.length) paragraphs.push(current.join(' '));
  return paragraphs;
}

function SlidesView({ deckId, sector, dataState, assetsBaseUrl, registerNavHandlers, motionDisabled }) {
  const sectorParam = sector ? String(sector).toLowerCase() : null;
  const isMobileView = typeof window !== 'undefined' && window.innerWidth <= 768;

  const mobileStateKey = useMemo(() => {
    const id = deckId || '2029';
    return `predictions.mobileState.${String(id)}`;
  }, [deckId]);

  const templatePack = useMemo(() => getSlidesTemplatePack(deckId), [deckId]);

  const imgPanelRef = useRef(null);
  const nodeGraphCanvasRef = useRef(null);
  const particleCanvasRef = useRef(null);
  const particleBurstRef = useRef(null);
  const nodeGraphSetHighlightRef = useRef(null);
  const itemRef = useRef(null);
  const paragraphSeedRef = useRef(0);
  const transitionTimeoutRef = useRef(null);
  const shakeTimeoutRef = useRef(null);
  const stageRef = useRef(null);

  const titleRef = useRef(null);
  const storyRef = useRef(null);
  const logicRef = useRef(null);
  const signalValidationRef = useRef(null);
  const textScrollRef = useRef(null);
  const typewriterIntervalsRef = useRef(new WeakMap());

  const viewedSlidesRef = useRef(new Set());

  const slides = useMemo(() => {
    if (!dataState || dataState.status !== 'ready') return [];
    const root = dataState.data ? dataState.data.root : null;
    const countermeasures = dataState.data ? dataState.data.countermeasures : null;
    const templateSequence = dataState.data ? dataState.data.templateSequence : null;
    return buildEnhancedSlides({ root, countermeasures, sectorParam, templateSequence });
  }, [dataState, sectorParam]);

  const [index, setIndex] = useState(0);
  const [mitigationState, setMitigationState] = useState('baseline');
  const [showTimeline, setShowTimeline] = useState(false);
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const [showSwipeHint, setShowSwipeHint] = useState(false);
  const [labQueryString, setLabQueryString] = useState(() => {
    if (typeof window === 'undefined') return '';
    return window.location.search || '';
  });
  const autoAdvanceIntervalRef = useRef(null);

  const item = slides && slides.length > 0 ? slides[Math.min(index, slides.length - 1)] : null;

  const findSectorLandingIndex = useCallback(
    (targetSector) => {
      if (!Array.isArray(slides) || slides.length === 0) return 0;
      const t = targetSector ? String(targetSector) : '';
      if (!t) return 0;
      const idx = slides.findIndex((s) => s && s.type === 'landing' && String(s.sector || '') === t);
      return idx >= 0 ? idx : 0;
    },
    [slides]
  );

  const onBackToSector = useCallback(() => {
    if (!item) return;
    const t = item.sector ? String(item.sector) : '';
    const landingIdx = findSectorLandingIndex(t);
    setIndex((prev) => {
      if (!slides || slides.length === 0) return prev;
      return Math.max(0, Math.min(slides.length - 1, landingIdx));
    });
    setMitigationState('baseline');
  }, [findSectorLandingIndex, item, slides]);

  const changeSlide = useCallback(
    (delta) => {
      if (!slides || slides.length === 0) return;
      setIndex((prev) => {
        const next = Math.max(0, Math.min(slides.length - 1, prev + delta));
        return next;
      });
      setMitigationState('baseline');
    },
    [slides]
  );

  useSwipeNavigation({
    onSwipeLeft: () => changeSlide(1),
    onSwipeRight: () => changeSlide(-1)
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hasTouch =
      'ontouchstart' in window ||
      (navigator && navigator.maxTouchPoints > 0) ||
      (window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
    if (!hasTouch) return;

    setShowSwipeHint(true);
    const hideHint = () => setShowSwipeHint(false);
    const onPointerDown = (e) => {
      if (!e) return;
      if (e.pointerType && e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
      hideHint();
    };
    const timeoutId = window.setTimeout(hideHint, 3500);
    window.addEventListener('touchstart', hideHint, { passive: true, once: true });
    window.addEventListener('pointerdown', onPointerDown, { passive: true, once: true });

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('touchstart', hideHint);
      window.removeEventListener('pointerdown', onPointerDown);
    };
  }, []);

  const setSlideIndex = useCallback(
    (nextIndex) => {
      if (!slides || slides.length === 0) return;
      const clamped = Math.max(0, Math.min(slides.length - 1, nextIndex));
      setIndex(clamped);
      setMitigationState('baseline');
    },
    [slides]
  );

  const toggleMitigation = useCallback(() => {
    if (!item || !item.mitigated_story) return;
    setMitigationState((s) => (s === 'baseline' ? 'mitigated' : 'baseline'));
  }, [item]);

  useEffect(() => {
    if (typeof registerNavHandlers !== 'function') return;
    registerNavHandlers({
      prev: () => changeSlide(-1),
      next: () => changeSlide(1),
      back: onBackToSector,
      mit: () => toggleMitigation(),
      state: {
        canPrev: !!(slides && slides.length > 0 && index > 0),
        canNext: !!(slides && slides.length > 0 && index < slides.length - 1),
        canBack: !!(item && item.sector),
        canMit: !!(item && item.mitigated_story)
      }
    });
    return () => {
      try {
        registerNavHandlers(null);
      } catch (_e) {
      }
    };
  }, [changeSlide, index, item, onBackToSector, registerNavHandlers, slides, toggleMitigation]);

  useEffect(() => {
    itemRef.current = item;
  }, [item]);

  const getSectorColor = useCallback(() => {
    const sRaw = itemRef.current && itemRef.current.sector ? String(itemRef.current.sector) : '';
    const s = sRaw.toUpperCase();
    if (s.includes('SCREEN')) return '#FF6B6B';
    if (s.includes('GAMES')) return '#4ECDC4';
    if (s.includes('MUSIC')) return '#A78BFA';
    if (s.includes('FASHION')) return '#EC4899';
    if (s.includes('CREATOR')) return '#10B981';
    return '#ffff00';
  }, []);

  const typewriter = useCallback(
    (el, text, speed = 20) => {
      if (!el || !text) return;
      const intervals = typewriterIntervalsRef.current;
      if (intervals.has(el)) {
        clearInterval(intervals.get(el));
      }
      el.classList.add('typing');
      el.textContent = '';
      let i = 0;
      const interval = setInterval(() => {
        if (i < text.length) {
          el.textContent += text.charAt(i);
          i++;
        } else {
          clearInterval(interval);
          intervals.delete(el);
          el.classList.remove('typing');
        }
      }, speed);
      intervals.set(el, interval);
    },
    []
  );

  useEffect(() => {
    setIndex(0);
    setMitigationState('baseline');
    setShowTimeline(false);
    setShowKeyboardHelp(false);
    if (autoAdvanceIntervalRef.current) {
      clearInterval(autoAdvanceIntervalRef.current);
      autoAdvanceIntervalRef.current = null;
    }
  }, [sectorParam, deckId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPopState = () => setLabQueryString(window.location.search || '');
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const labParams = useMemo(() => {
    if (typeof window === 'undefined') return new URLSearchParams();
    return new URLSearchParams(labQueryString || window.location.search || '');
  }, [labQueryString]);

  const updateLabParams = useCallback(
    (mutate) => {
      if (typeof window === 'undefined') return;
      const next = new URLSearchParams(labParams.toString());
      mutate(next);
      const qs = next.toString();
      const nextSearch = qs ? `?${qs}` : '';
      window.history.replaceState(null, '', `${window.location.pathname}${nextSearch}${window.location.hash || ''}`);
      setLabQueryString(nextSearch);
    },
    [labParams]
  );

  const labEnabled = labParams.get('lab') === '1';
  const forcedTemplate = labParams.get('tpl') ? String(labParams.get('tpl')) : '';
  const sirensEnabled = labParams.get('sirens') === '1';
  const forcedIdxRaw = labParams.get('idx');
  const forcedIdx = forcedIdxRaw != null && String(forcedIdxRaw).trim() !== '' ? Number(forcedIdxRaw) : null;

  useEffect(() => {
    if (!slides || slides.length === 0) return;
    if (forcedIdx == null || Number.isNaN(forcedIdx)) return;
    setIndex(Math.max(0, Math.min(slides.length - 1, Math.floor(forcedIdx))));
    setMitigationState('baseline');
  }, [forcedIdx, slides]);

  useEffect(() => {
    if (motionDisabled) return undefined;
    const canvasEl = particleCanvasRef.current;
    if (!canvasEl) return;

    const { burst, cleanup } = startParticleSystem({
      canvasEl,
      getColor: getSectorColor
    });

    particleBurstRef.current = burst;
    return () => {
      particleBurstRef.current = null;
      cleanup();
    };
  }, [getSectorColor, motionDisabled]);

  useEffect(() => {
    if (motionDisabled) return undefined;
    const canvasEl = nodeGraphCanvasRef.current;
    if (!canvasEl) return;
    const { setHighlight, cleanup } = startNodeGraphEffect({ canvasEl });
    nodeGraphSetHighlightRef.current = setHighlight;
    return () => {
      nodeGraphSetHighlightRef.current = null;
      cleanup();
    };
  }, [dataState && dataState.status, motionDisabled]);

  useEffect(() => {
    if (motionDisabled) return undefined;
    const container = document.getElementById('mainContainer');
    if (!container) return;
    const stageEl = stageRef.current;

    const transitions = ['transition-wipe', 'transition-zoom', 'transition-glitch-intense', 'transition-shatter'];
    const transition = transitions[Math.floor(Math.random() * transitions.length)];

    container.classList.add(transition);
    if (stageEl) {
      stageEl.classList.add('shake-pulse');
      if (shakeTimeoutRef.current) {
        clearTimeout(shakeTimeoutRef.current);
      }
      shakeTimeoutRef.current = setTimeout(() => {
        stageEl.classList.remove('shake-pulse');
      }, 500);
    }
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
    }
    transitionTimeoutRef.current = setTimeout(() => {
      container.classList.remove(transition);
    }, 1200);

    if (typeof particleBurstRef.current === 'function') {
      particleBurstRef.current();
    }

    return () => {
      container.classList.remove(transition);
      if (stageEl) {
        stageEl.classList.remove('shake-pulse');
      }
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
        transitionTimeoutRef.current = null;
      }
      if (shakeTimeoutRef.current) {
        clearTimeout(shakeTimeoutRef.current);
        shakeTimeoutRef.current = null;
      }
    };
  }, [index, motionDisabled]);

  useEffect(() => {
    viewedSlidesRef.current.add(index);
  }, [index]);

  useEffect(() => {
    paragraphSeedRef.current = (Math.random() * 0xffffffff) >>> 0;
  }, [index, mitigationState]);

  useEffect(() => {
    const el = textScrollRef.current;
    if (el) {
      el.scrollTop = 0;
    }
  }, [index]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!slides || slides.length === 0) return;
    if (forcedIdx != null && !Number.isNaN(forcedIdx)) return;

    let raw;
    try {
      raw = window.sessionStorage.getItem(mobileStateKey);
    } catch (_e) {
      raw = null;
    }
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw);
      const nextIdx = Math.max(0, Math.min(slides.length - 1, Math.floor(Number(parsed.index || 0))));
      setIndex(nextIdx);
      setMitigationState(parsed.mitigationState === 'mitigated' ? 'mitigated' : 'baseline');
    } catch (_e) {
    }

    try {
      window.sessionStorage.removeItem(mobileStateKey);
    } catch (_e) {
    }
  }, [forcedIdx, mobileStateKey, slides]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (typeof registerNavHandlers === 'function' && (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        return;
      }

      if (e.key === 'ArrowLeft') changeSlide(-1);
      else if (e.key === 'ArrowRight') changeSlide(1);
      else if (e.key === '1') setSlideIndex(1);
      else if (e.key === '2') setSlideIndex(6);
      else if (e.key === '3') setSlideIndex(11);
      else if (e.key === '4') setSlideIndex(16);
      else if (e.key === '5') setSlideIndex(21);
      else if (e.key === 't' || e.key === 'T') setShowTimeline((v) => !v);
      else if (e.key === '?') setShowKeyboardHelp((v) => !v);
      else if (e.key === 'Escape') {
        setShowTimeline(false);
        setShowKeyboardHelp(false);
      } else if ((e.key === 'o' || e.key === 'O') && (e.ctrlKey || e.metaKey) && e.shiftKey) {
        e.preventDefault();
        updateLabParams((p) => {
          if (p.get('lab') === '1') {
            p.delete('lab');
          } else {
            p.set('lab', '1');
          }
        });
      } else if (e.key === ' ') {
        e.preventDefault();

        if (autoAdvanceIntervalRef.current) {
          clearInterval(autoAdvanceIntervalRef.current);
          autoAdvanceIntervalRef.current = null;
        } else {
          autoAdvanceIntervalRef.current = setInterval(() => {
            setIndex((prev) => {
              const next = prev + 1;
              if (!slides || slides.length === 0) return prev;
              if (next >= slides.length) {
                if (autoAdvanceIntervalRef.current) {
                  clearInterval(autoAdvanceIntervalRef.current);
                  autoAdvanceIntervalRef.current = null;
                }
                return prev;
              }
              return next;
            });
            setMitigationState('baseline');
          }, 5000);
        }
      } else if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && typeof registerNavHandlers !== 'function') {
        e.preventDefault();
        toggleMitigation();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [changeSlide, registerNavHandlers, setSlideIndex, slides, toggleMitigation, updateLabParams]);

  useEffect(() => {
    return () => {
      if (autoAdvanceIntervalRef.current) {
        clearInterval(autoAdvanceIntervalRef.current);
        autoAdvanceIntervalRef.current = null;
      }
    };
  }, []);

  const effectiveTemplateName = useMemo(() => {
    if (!item) return '';
    const base = item && item.template ? String(item.template) : '';
    const byType = item.type === 'global' ? 'title' : base;

    if (forcedTemplate) return forcedTemplate;

    return byType;
  }, [item, isMobileView, forcedTemplate, templatePack]);

  const imagePanelHtml = useMemo(() => {
    if (!item) return '';
    if (!templatePack || typeof templatePack.renderSlideTemplateHtml !== 'function') return '';
    return templatePack.renderSlideTemplateHtml({ templateName: effectiveTemplateName, item, assetsBaseUrl });
  }, [effectiveTemplateName, item, assetsBaseUrl, templatePack]);

  const templateName = effectiveTemplateName;
  const isFullBleedTemplate =
    (templatePack && templatePack.fullBleedTemplateNames && templatePack.fullBleedTemplateNames.has(templateName)) ||
    templateName === 'title' ||
    templateName === 'sector-landing' ||
    templateName === 'sector-model' ||
    templateName === 'sector-cta' ||
    (item && (item.type === 'global' || item.type === 'landing' || item.type === 'model' || item.type === 'cta'));

  const templateNumber = useMemo(() => {
    const names = templatePack && Array.isArray(templatePack.templateNames) ? templatePack.templateNames : null;
    if (!names) return 0;
    const idx = names.indexOf(templateName);
    return idx >= 0 ? idx + 1 : 0;
  }, [templatePack, templateName]);

  const textLayout = useMemo(() => {
    if (!item || item.type !== 'prediction') return 'bl';
    if (isFullBleedTemplate) return 'bl';

    if (isMobileView) return 'bl';

    const seed = `${item.sector || ''}::${item.title || ''}::${templateName || ''}`;
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;

    const variants = ['bl', 'br', 'ml', 'mr', 'tl', 'tr'];
    return variants[h % variants.length];
  }, [item, templateName, isFullBleedTemplate, isMobileView]);

  const showMitigationToggle = !!(item && item.type === 'prediction' && item.mitigated_story);

  const storyText = useMemo(() => {
    if (!item) return '';
    if (item.type !== 'prediction') return item.story || '';
    if (mitigationState === 'mitigated' && item.mitigated_story) return item.mitigated_story;
    return item.story || '';
  }, [item, mitigationState]);

  useEffect(() => {
    const el = signalValidationRef.current;
    if (!el) return;

    // Hide by default
    el.innerHTML = '';
    el.style.display = 'none';

    if (!item || item.type !== 'prediction' || !item.signal_validation) return;

    const strengthColors = {
      HIGH: '#ff0000',
      'MEDIUM-HIGH': '#ff6600',
      MEDIUM: '#ffaa00',
      'LOW-MEDIUM': '#ffdd00',
      WEAK: '#888888',
      LOW: '#666666'
    };

    const strength = item.signal_strength ? String(item.signal_strength) : '';
    const strengthColor = strengthColors[strength] || '#ffff00';

    el.style.display = 'block';
    el.innerHTML = `
      <div class="signal-header" id="signalToggleHeader" style="cursor: pointer; display: flex; align-items: center; gap: 10px; padding: 10px 0; border-top: 1px solid rgba(255,255,0,0.3); margin-top: 15px;">
        <span style="font-size: 1.2rem;">📊</span>
        <span style="color: var(--safety-yellow); font-weight: bold; font-size: 0.9rem; letter-spacing: 1px;">SIGNAL STRENGTH:</span>
        <span style="color: ${strengthColor}; font-weight: bold; font-size: 0.9rem; padding: 3px 8px; border: 1px solid ${strengthColor}; border-radius: 3px;">${strength}</span>
        <span id="signalToggleIcon" style="margin-left: auto; color: var(--safety-yellow); font-size: 1rem;">▲</span>
      </div>
      <div id="signalContent" style="display: block; padding: 10px 0; color: rgba(255,255,255,0.85); font-size: 0.85rem; line-height: 1.6; border-bottom: 1px solid rgba(255,255,0,0.3); margin-bottom: 15px;">
        ${String(item.signal_validation)}
      </div>
    `;

    const header = el.querySelector('#signalToggleHeader');
    const content = el.querySelector('#signalContent');
    const icon = el.querySelector('#signalToggleIcon');
    if (!header || !content || !icon) return;

    const onToggle = (e) => {
      if (e && e.preventDefault) e.preventDefault();
      if (content.style.display === 'none') {
        content.style.display = 'block';
        icon.textContent = '▲';
      } else {
        content.style.display = 'none';
        icon.textContent = '▼';
      }
    };

    header.addEventListener('click', onToggle);
    return () => {
      header.removeEventListener('click', onToggle);
    };
  }, [item]);

  useEffect(() => {
    if (!item || isFullBleedTemplate) return;
    const titleEl = titleRef.current;
    const storyEl = storyRef.current;
    const logicEl = logicRef.current;

    if (titleEl) typewriter(titleEl, item.title || '', 5);

    if (storyEl) {
      storyEl.innerHTML = '';
      const paras = createLegacyParagraphs(storyText, paragraphSeedRef.current);
      paras.forEach((paraText, idx) => {
        const p = document.createElement('p');
        if (idx > 0) p.style.marginTop = '15px';
        storyEl.appendChild(p);
        setTimeout(() => typewriter(p, paraText, item.type === 'prediction' ? 8 : 3), 150 + idx * 50);
      });
    }

    if (logicEl) {
      const logicText = item.logic ? String(item.logic).replace(/\n/g, ' ') : '';
      if (logicText) {
        logicEl.textContent = '';
        setTimeout(() => typewriter(logicEl, logicText, 12), 300);
      } else {
        logicEl.textContent = '';
      }
    }

    if (typeof nodeGraphSetHighlightRef.current === 'function' && item.logic) {
      const nodeMatch = String(item.logic).match(/NODE:\s*([a-z_]+)/i);
      nodeGraphSetHighlightRef.current(nodeMatch ? nodeMatch[1] : '');
    }
  }, [item, storyText, typewriter, isFullBleedTemplate, index, mitigationState]);

  const crawlHtml = useMemo(() => {
    const cm = item && item.countermeasures ? item.countermeasures : null;
    if (!cm || !Array.isArray(cm.actions) || cm.actions.length === 0) {
      return '<span style="color: var(--safety-yellow); margin: 0 20px;">⚠ NO COUNTERMEASURES AVAILABLE</span>';
    }

    let crawlText = '';
    crawlText += `<span class="status">${cm.status || ''}</span>`;
    crawlText += `<span class="window">${cm.window || ''}</span>`;
    crawlText += ' ⚠⚠⚠ ';
    cm.actions.forEach((action, aidx) => {
      const label = action && action.label ? action.label : '';
      const odds = action && action.odds ? action.odds : '';
      const agent = action && action.agent ? action.agent : '';
      const brief = action && action.brief ? action.brief : '';
      crawlText += `<span class="action">${label} <span class="odds">${odds}</span> → ${agent} → ${brief}</span>`;
      if (aidx < cm.actions.length - 1) crawlText += ' ⚠ ';
    });
    crawlText += ` ⚠⚠⚠ COMBINED SUCCESS: <span class="odds">${cm.combined || ''}</span>`;
    crawlText += ' ⚠⚠⚠ ';

    return crawlText + crawlText + crawlText;
  }, [item]);

  const scrubberBlocks = useMemo(() => {
    const blocks = [];
    if (!Array.isArray(slides) || slides.length === 0) return blocks;

    const sectors = [];
    let currentSector = null;
    let sectorStart = 0;
    for (let i = 0; i < slides.length; i++) {
      const s = slides[i];
      const sectorName = s && s.sector ? s.sector : '';
      if (sectorName !== currentSector) {
        if (currentSector !== null) {
          sectors.push({ sector: currentSector, start: sectorStart, end: i - 1 });
        }
        currentSector = sectorName;
        sectorStart = i;
      }
    }
    if (currentSector !== null) {
      sectors.push({ sector: currentSector, start: sectorStart, end: slides.length - 1 });
    }

    sectors.forEach((s, sectorIdx) => {
      const count = s.end - s.start + 1;
      for (let i = 0; i < count; i++) {
        const slideIdx = s.start + i;
        const slideData = slides[slideIdx];
        const isYellow = true;

        const isSpecial = !!(
          slideData &&
          (slideData.type === 'landing' || slideData.type === 'model' || slideData.type === 'cta')
        );
        const specialBg =
          slideData && slideData.type === 'landing'
            ? '#FF6600'
            : slideData && slideData.type === 'model'
              ? '#00F3FF'
              : slideData && slideData.type === 'cta'
                ? '#00FF00'
                : null;

        const tooltipText = slideData
          ? slideData.type === 'landing'
            ? `${slideData.sector} - Sector Overview`
            : slideData.type === 'model'
              ? `${slideData.sector} - The Model`
              : slideData.type === 'cta'
                ? `${slideData.sector} - Next Steps`
                : `${slideData.sector}: ${slideData.title}`
          : '';

        blocks.push({
          slideIdx,
          isYellow,
          isSpecial,
          specialBg,
          tooltipText,
          sectorIdx,
          isFirstInSector: i === 0,
          isFirstSector: sectorIdx === 0
        });
      }
    });

    return blocks;
  }, [slides]);

  const fallbackImgSrc = useMemo(() => {
    if (!item) return '';
    const resolver = templatePack && typeof templatePack.resolveDeckAssetUrl === 'function' ? templatePack.resolveDeckAssetUrl : null;
    if (resolver) return resolver(item.img, assetsBaseUrl);
    if (!item.img) return '';
    const s = String(item.img);
    if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('/')) return s;
    const base = assetsBaseUrl ? String(assetsBaseUrl) : '';
    if (!base) return s;
    if (base.endsWith('/')) return `${base}${s}`;
    return `${base}/${s}`;
  }, [item, assetsBaseUrl, templatePack]);

  useEffect(() => {
    if (motionDisabled) return undefined;
    if (!item) return;
    const imgPanelEl = imgPanelRef.current;
    if (!imgPanelEl) return;

    const cleanups = [];
    const addCleanup = (fn) => {
      if (typeof fn === 'function') cleanups.push(fn);
    };

    if (imgPanelEl.querySelector('canvas.dash-canvas[data-kind="impact-trend"]')) {
      addCleanup(startDataDashboardEffect({ imgPanelEl, item }));
    }
    if (templateName === 'quantum') {
      addCleanup(startQuantumEffect({ imgPanelEl }));
    }
    if (templateName === 'cosmic') {
      addCleanup(startCosmicEffect({ imgPanelEl }));
    }
    if (templateName === 'matrix-rain') {
      addCleanup(startMatrixRainEffect({ imgPanelEl }));
    }
    if (templateName === 'broadcast-interrupt') {
      addCleanup(startBroadcastInterruptEffect({ imgPanelEl }));
    }
    if (templateName === 'wheat-paste') {
      addCleanup(startWheatPasteEffect({ imgPanelEl }));
    }
    if (templateName === 'stencil-spray') {
      addCleanup(startStencilSprayEffect({ imgPanelEl }));
    }
    if (templateName === 'teletype-assemble') {
      addCleanup(startTeletypeAssembleEffect({ imgPanelEl }));
    }
    if (templateName === 'comparison-split') {
      addCleanup(startComparisonSplitEffect({ imgPanelEl }));
    }
    if (templateName === 'factory-stamp') {
      addCleanup(startFactoryStampEffect({ imgPanelEl }));
    }
    if (templateName === 'title') {
      addCleanup(startCountingEffect(imgPanelEl));
      addCleanup(startSystemBootEffect(imgPanelEl));
    }
    if (templateName === 'sector-landing') {
      addCleanup(startDangerZoneEffect(imgPanelEl));
    }
    if (templateName === 'sector-model') {
      addCleanup(startModelAnalysisEffect(imgPanelEl));
    }
    if (templateName === 'terminal') {
      addCleanup(startTerminalTypingEffect(imgPanelEl));
    }
    if (sirensEnabled) {
      addCleanup(startPanicSirensEffect({ imgPanelEl }));
    }

    if (cleanups.length === 0) return undefined;
    return () => {
      cleanups.forEach((fn) => {
        try {
          fn();
        } catch (_e) {
        }
      });
    };
  }, [item, templateName, sirensEnabled, imagePanelHtml, motionDisabled]);

  const onGridView = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      try {
        window.sessionStorage.setItem(
          mobileStateKey,
          JSON.stringify({ index, mitigationState, ts: Date.now() })
        );
      } catch (_e) {
      }

      const params = new URLSearchParams(window.location.search);
      params.set('view', 'grid');
      params.set('impl', 'react');
      window.location.href = `/predictions/${encodeURIComponent(String(deckId || '2029'))}${
        params.toString() ? `?${params.toString()}` : ''
      }`;
    },
    [deckId, index, mitigationState, mobileStateKey]
  );

  return (
    <div className="pred-slides-root">
      <canvas id="nodeGraphCanvas" className="node-graph-bg" ref={nodeGraphCanvasRef} />
      <div className="crt-overlay" />
      <canvas id="particleCanvas" ref={particleCanvasRef} />

      {dataState && dataState.status === 'loading' && (
        <div style={{ padding: 24, color: '#ffff00' }}>Loading slides…</div>
      )}

      {(!dataState || dataState.status === 'ready') && (
        <>
          <div className="countermeasures-crawl">
            <div className="crawl-content" id="crawlContent" dangerouslySetInnerHTML={{ __html: crawlHtml }} />
          </div>

          <div className="slide-scrubber">
            <div className={`pred-swipe-hint ${showSwipeHint ? 'show' : ''}`}>Swipe to navigate</div>
            <div
              className="slide-scrubber-label"
              id="sectorName"
              style={{ textTransform: 'uppercase', letterSpacing: 2 }}
            >
              {item && item.sector ? item.sector : ''}
            </div>
            <div id="sectorBlocks" className="sector-blocks">
              {scrubberBlocks.map((b) => {
                const isViewed = viewedSlidesRef.current.has(b.slideIdx);
                const isActive = b.slideIdx === index;

                let background;
                if (b.isSpecial && b.specialBg) {
                  background = b.specialBg;
                } else if (isActive) {
                  background = b.isYellow ? '#000' : 'var(--safety-yellow)';
                } else if (b.isYellow) {
                  background = 'var(--safety-yellow)';
                } else {
                  background = isViewed ? '#242424' : '#000';
                }

                const opacity = !b.isSpecial && b.isYellow ? (isViewed && !isActive ? 0.45 : 1) : 1;

                return (
                  <div
                    key={b.slideIdx}
                    className={`sector-block${isActive ? ' active' : ''}${isViewed ? ' viewed' : ''}${
                      b.isSpecial ? ' special-block' : ''
                    }`}
                    data-tooltip={b.tooltipText}
                    style={{
                      background,
                      opacity,
                      width: b.isSpecial ? 12 : undefined,
                      marginLeft: b.isFirstInSector && !b.isFirstSector ? 20 : undefined
                    }}
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSlideIndex(b.slideIdx);
                    }}
                  />
                );
              })}
            </div>
            <div className="slide-scrubber-info">
              <span id="sliderCurrent">{String(Math.min(index + 1, slides.length)).padStart(2, '0')}</span> /{' '}
              <span id="sliderTotal">{String(slides.length).padStart(2, '0')}</span>
              <span style={{ marginLeft: 10, opacity: 0.75 }}>
                • tpl {templateNumber ? String(templateNumber).padStart(2, '0') : '??'} • {templateName || 'unknown'}
              </span>
            </div>
          </div>

          <div className={`pred-slides-timeline-scrubber ${showTimeline ? '' : 'hidden'}`}>
            <div className="pred-slides-timeline-dots">
              {slides.map((_, idx) => (
                <div
                  key={idx}
                  className={`pred-slides-timeline-dot ${idx === index ? 'active' : ''}`}
                  onClick={() => setSlideIndex(idx)}
                  role="button"
                  tabIndex={0}
                />
              ))}
            </div>
          </div>

          <div className={`pred-slides-keyboard-help ${showKeyboardHelp ? '' : 'hidden'}`}>
            <h3>KEYBOARD SHORTCUTS</h3>
            <div className="shortcut"><kbd>←/→</kbd> Navigate slides</div>
            <div className="shortcut"><kbd>↑/↓</kbd> Toggle mitigation (when available)</div>
            <div className="shortcut"><kbd>1-5</kbd> Jump (legacy positions)</div>
            <div className="shortcut"><kbd>T</kbd> Timeline scrubber</div>
            <div className="shortcut"><kbd>SPACE</kbd> Auto-advance</div>
            <div className="shortcut"><kbd>?</kbd> Toggle this help</div>
            <div className="shortcut"><kbd>ESC</kbd> Close overlays</div>
          </div>

          <div className="pred-slides-stage" ref={stageRef}>
            <div
              className={`pred-slides-container container ${isFullBleedTemplate ? 'no-text' : ''} ${
                mitigationState === 'mitigated' ? 'mitigated' : ''
              }`}
              id="mainContainer"
              data-template={templateName}
              data-text-layout={textLayout}
              data-has-sirens={sirensEnabled ? 'true' : undefined}
            >
              <div className="scanlines" />
              {imagePanelHtml ? (
                <div
                  className="pred-slides-image-panel image-panel"
                  id="imgPanel"
                  ref={imgPanelRef}
                  dangerouslySetInnerHTML={{ __html: imagePanelHtml }}
                />
              ) : (
                <div className="pred-slides-image-panel image-panel" id="imgPanel" ref={imgPanelRef}>
                  {item && <img alt={item.title} src={fallbackImgSrc} />}
                </div>
              )}

              {!isFullBleedTemplate && (
                <div className="pred-slides-text-panel text-panel">
                  <div className="text-content">
                <h1>
                  <span className="signal-number" id="sigNum">
                    {String(Math.min(index + 1, slides.length)).padStart(2, '0')}/{String(slides.length).padStart(2, '0')}
                  </span>{' '}
                  <span id="sectorNameHeader">{item ? item.sectorDisplayName || item.sector : ''}</span>
                </h1>
                <h2 id="title" ref={titleRef} />
                <div className="pred-slides-text-scroll" ref={textScrollRef}>
                  <div id="story" ref={storyRef} className="pred-slides-story" />
                  <div className="model-logic" id="logic" ref={logicRef} />
                  <div
                  id="evidenceAndMitigation"
                  style={{ marginTop: 20, paddingTop: 15, borderTop: '1px solid rgba(255,255,0,0.2)' }}
                >
                  <div id="signalValidation" ref={signalValidationRef} />
                </div>

                  {showMitigationToggle && (
                    <div style={{ marginTop: 14 }}>
                      <button
                        id="toggleMitigation"
                        type="button"
                        className={`pred-slides-mitigation-toggle mitigation-toggle ${mitigationState === 'mitigated' ? 'mitigated' : ''}`}
                        style={{ display: 'inline-block' }}
                        onClick={toggleMitigation}
                      >
                        {mitigationState === 'mitigated' ? '⚠ HIDE MITIGATION' : '⚠ SHOW MITIGATION'}
                      </button>
                    </div>
                  )}
                </div>
                  </div>
                </div>
              )}

              {labEnabled && (
                <div
                  className="pred-slides-template-lab"
                  style={{
                    position: 'absolute',
                    top: 20,
                    left: 20,
                    zIndex: 2500,
                    background: 'rgba(0,0,0,0.75)',
                    border: '1px solid rgba(255,255,0,0.35)',
                    padding: '10px 12px',
                    fontFamily: 'Courier New, Courier, monospace',
                    fontSize: 12,
                    color: 'var(--safety-yellow)',
                    maxWidth: 420
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 8, letterSpacing: 1 }}>TEMPLATE LAB</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{ opacity: 0.85 }}>tpl</span>
                      <select
                        value={forcedTemplate}
                        onChange={(e) => {
                          const v = e.target.value;
                          updateLabParams((p) => {
                            if (!v) p.delete('tpl');
                            else p.set('tpl', v);
                          });
                        }}
                      >
                        <option value="">(slide default)</option>
                        {(templatePack && Array.isArray(templatePack.templateNames) ? templatePack.templateNames : []).map(
                          (name, index) => (
                            <option key={name} value={name}>
                              {index + 1}. {name}
                            </option>
                          )
                        )}
                      </select>
                    </label>

                    <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={sirensEnabled}
                        onChange={(e) => {
                          updateLabParams((p) => {
                            if (e.target.checked) p.set('sirens', '1');
                            else p.delete('sirens');
                          });
                        }}
                      />
                      <span style={{ opacity: 0.9 }}>sirens</span>
                    </label>

                    <button
                      type="button"
                      style={{
                        background: 'transparent',
                        color: 'var(--safety-yellow)',
                        border: '1px solid rgba(255,255,0,0.5)',
                        padding: '2px 6px',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        updateLabParams((p) => {
                          p.set('idx', String(index));
                        });
                      }}
                    >
                      pin idx
                    </button>

                    <button
                      type="button"
                      style={{
                        background: 'transparent',
                        color: 'var(--safety-yellow)',
                        border: '1px solid rgba(255,255,0,0.5)',
                        padding: '2px 6px',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        updateLabParams((p) => {
                          p.delete('tpl');
                          p.delete('idx');
                          p.delete('sirens');
                        });
                      }}
                    >
                      reset
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default SlidesView;
